import {
  Badge,
  Button,
  Container,
  Flex,
  Grid,
  Paper,
  Text,
  Anchor,
  Title,
  Box,
  Stack,
  Divider,
  Group,
  Checkbox,
  Tooltip,
} from "@mantine/core";
import "@mantine/dates/styles.css";
import { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  DateTimePicker,
  MultiSelect,
  Select,
  TextInput,
} from "react-hook-form-mantine";
import { fullFormats } from "ajv-formats/dist/formats";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  IconCalendarTime,
  IconUsers,
  IconBuilding,
  IconTags,
  IconMapPin,
  IconLink,
  IconPlus,
  IconEye,
} from "@tabler/icons-react";

import { formatDateTime } from "./utils/formatDateTime";
import { formatTagLabel } from "./utils/formatTagLabel";
import AddLinkModal from "./Edit/AddLinkModal";
import { useConfirm } from "../../hooks/useConfirm";
import SessionLinkCard from "./SessionLinkCard";
import SessionDescriptionEditor from "./SessionDescriptionEditor";
import { web_sessionFormSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { mergeI18nWithDefaults } from "../../utils/i18nUtils";
import { isRichTextEmpty } from "@congrs/utils";

const tagSubtypes = ["dressCode", "sessionType", "topic"];

function SessionForm({
  mode = "create", // 'create' or 'edit'
  data = {},
  enableActions = true,
  speakerOptions = [],
  sponsorOptions = [],
  tagOptions = [],
  categoryTagOptions = [],
  sessionTags = [],
  locationsOptions = [],
  event,
  onSubmit: onSubmitProp,
  onCancel,
  onDeleteLink,
  onCreateLink,
  isLoading,
}) {
  const {
    titleI18n,
    descriptionI18n,
    descriptionRichI18n,
    starting,
    ending,
    location,
    speakers,
    sponsorId,
    canceledAt,
    isClickable = true,
    targetedTags = [],
    links = [],
  } = data;

  const categorySelectOptions = categoryTagOptions.map((tag) => ({
    label: tag.title,
    value: tag.id.toString(),
  }));

  const defaultSponsor = sponsorOptions?.find(
    (s) => Number(s.value) === sponsorId
  );

  const extractTagIdsBySubtype = (tags, subtype) =>
    tags
      ?.filter((tag) => tag.subtype === subtype)
      .map((tag) => tag.id.toString()) || [];

  const defaultTags = tagSubtypes.reduce((acc, subtype) => {
    acc[subtype] = extractTagIdsBySubtype(sessionTags, subtype);
    return acc;
  }, {});

  const formatTagOptions = (tags, subtype) =>
    tags
      ?.filter((tag) => tag.subtype === subtype)
      .map((tag) => ({
        label: tag.title,
        value: tag.id.toString(),
      })) || [];

  const tagSelectOptions = tagSubtypes.reduce((acc, subtype) => {
    acc[subtype] = formatTagOptions(tagOptions, subtype);
    return acc;
  }, {});

  const mainLanguage = event?.language;
  const otherLanguages = event?.secondaryLanguages;
  // We have to filter out null and undefined values because object will contain
  // undefined: undefined keys and schema will fail
  const allLanguages = [mainLanguage, ...(otherLanguages || [])].filter(
    Boolean
  );
  const showLanguageLabels = allLanguages.length > 1;

  // Parse descriptionRichI18n if it comes as strings from API
  const parsedDescriptionRichI18n = {};
  if (descriptionRichI18n) {
    Object.keys(descriptionRichI18n).forEach((lang) => {
      const content = descriptionRichI18n[lang];
      if (typeof content === "string") {
        try {
          parsedDescriptionRichI18n[lang] = JSON.parse(content);
        } catch (e) {
          parsedDescriptionRichI18n[lang] = null;
        }
      } else {
        parsedDescriptionRichI18n[lang] = content;
      }
    });
  }

  const fromPlainToTiptap = (text) => {
    if (!text?.trim()) return null;
    const paragraphs = text.split(/\n{2,}/);
    const content = paragraphs.map((para) => {
      const lines = para.split("\n");
      const nodes = [];
      lines.forEach((line, idx) => {
        if (line.length) {
          nodes.push({ type: "text", text: line });
        }
        if (idx < lines.length - 1) {
          nodes.push({ type: "hardBreak" });
        }
      });
      return {
        type: "paragraph",
        content: nodes.length ? nodes : [{ type: "text", text: "" }],
      };
    });
    return { type: "doc", content };
  };

  const prefilledDescriptionRichI18n = {};
  allLanguages.forEach((lang) => {
    prefilledDescriptionRichI18n[lang] =
      parsedDescriptionRichI18n?.[lang] ||
      fromPlainToTiptap(descriptionI18n?.[lang]);
  });

  const defaultValues = {
    titleI18n: mergeI18nWithDefaults(titleI18n, mainLanguage, otherLanguages),
    descriptionRichI18n: prefilledDescriptionRichI18n,
    locationId: location?.id ? location.id.toString() : null,
    starting: starting ?? "",
    ending: ending ?? "",
    speakers: speakers?.map((s) => s.id.toString()) || [],
    tags: {
      dressCode: defaultTags.dressCode,
      sessionType: defaultTags.sessionType,
      topic: defaultTags.topic,
    },
    sponsorId: sponsorId ? sponsorId.toString() : null,
    targetedTagIds: targetedTags.map((tag) => tag.id.toString()),
    isClickable,
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    setValue,
    setError,
    watch,
    reset,
  } = useForm({
    defaultValues,
    resolver: resolver(web_sessionFormSchema, {
      allowUnionTypes: true,
      formats: fullFormats,
      coerceTypes: true,
    }),
    mode: "onBlur",
  });

  const confirm = useConfirm();

  const [start, setStart] = useState(starting ?? null);
  const [end, setEnd] = useState(ending ?? null);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);

  const onSubmit = (formData) => {
    const dataToSend = { ...formData };

    dataToSend.starting = formatDateTime(dayjs(start).toString());
    dataToSend.ending = formatDateTime(dayjs(end).toString());
    dataToSend.speakers = dataToSend.speakers.map((speaker) => Number(speaker));
    dataToSend.tags = tagSubtypes
      .reduce((acc, type) => acc.concat(dataToSend.tags[type] || []), [])
      .map((tag) => Number(tag));
    dataToSend.targetedTagIds = formData.targetedTagIds?.length
      ? formData.targetedTagIds.map(Number)
      : null;

    onSubmitProp(dataToSend, {
      onSuccess: () => {
        // If we're creating a session, reset the form to the default values (null values)
        // Otherwise, reset the form to the form data (the data that was submitted)
        reset(mode === "create" ? defaultValues : formData);

        // If we're creating a session, reset the start and end dates to null
        if (mode === "create") {
          setStart(null);
          setEnd(null);
        }
      },
    });
  };

  const dateIsAllowed = useCallback(
    (date) => {
      const allowedDates = event?.dates?.map((date) => date.date);
      return allowedDates?.includes(dayjs(date).format("YYYY-MM-DD"));
    },
    [event?.dates]
  );

  const [isClickableValue, descriptions, speakerIds, sponsorValue] = watch([
    "isClickable",
    "descriptionRichI18n",
    "speakers",
    "sponsorId",
  ]);
  const hasOptionalInfo =
    Object.values(descriptions || {}).some((d) => !isRichTextEmpty(d)) ||
    speakerIds?.length > 0 ||
    !!sponsorValue ||
    links.length > 0;
  const startingDate = watch("starting");
  const endingDate = watch("ending");

  const startingDateString = dayjs(startingDate).format("YYYY-MM-DD");
  const endingDateString = dayjs(endingDate).format("YYYY-MM-DD");

  useEffect(() => {
    if (startingDate) {
      if (!dateIsAllowed(startingDateString)) {
        setError("starting", {
          type: "onBlur",
          message: "Date is out of event dates. Please select a valid date",
        });
      } else {
        setError("starting", { type: "onBlur", message: undefined });
      }
    }

    if (endingDate) {
      if (!dateIsAllowed(endingDateString)) {
        setError("ending", {
          type: "manual",
          message: "Date is out of allowed dates. Please select a valid date",
        });
      } else {
        setError("ending", { type: "manual", message: undefined });
      }
    }
  }, [
    startingDate,
    endingDate,
    startingDateString,
    endingDateString,
    setError,
    dateIsAllowed,
  ]);

  const handleAddLink = (linkData) => {
    onCreateLink(linkData);
    setIsAddLinkModalOpen(false);
  };

  return (
    <Container size="xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex justify="space-between" gap="sm" mb="md">
          <Button
            type="submit"
            loading={isLoading}
            disabled={!enableActions || !isDirty}
            size="sm"
          >
            {mode === "create" ? "Create session" : "Edit session"}
          </Button>

          {mode === "edit" && onCancel && (
            <Button
              size="sm"
              color={canceledAt === null ? "red" : "green"}
              onClick={() => {
                confirm({
                  title: `${canceledAt ? "Restore" : "Cancel"} session?`,
                  message: `Are you sure you want to ${canceledAt ? "restore" : "cancel"} this session?`,
                  cancelLabel: "No",
                  onConfirm: () => onCancel(data),
                });
              }}
            >
              {canceledAt === null ? "Cancel session" : "Restore session"}
            </Button>
          )}
        </Flex>
        <Grid>
          {/* Left Column - Session Details */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder shadow="md" p="xl" radius="md">
              <Stack gap="md">
                <Box>
                  <Group justify="space-between" mb="md">
                    <Title
                      order={3}
                      display="flex"
                      style={{ alignItems: "center", gap: "8px" }}
                    >
                      <IconCalendarTime size={24} />
                      Session Details
                      {mode === "edit" && canceledAt !== null && (
                        <Badge color="red" variant="dot" size="md" ta="right">
                          Canceled
                        </Badge>
                      )}
                    </Title>
                    {mode === "edit" && (
                      <Tooltip
                        label="Please add more info to make the session open."
                        disabled={hasOptionalInfo}
                      >
                        <Box bg="blue.0" px="sm" py={6} bdrs="sm">
                          <Checkbox
                            label="Open session in Agenda"
                            checked={isClickableValue && hasOptionalInfo}
                            disabled={!enableActions || !hasOptionalInfo}
                            onChange={(e) =>
                              setValue("isClickable", e.currentTarget.checked, {
                                shouldDirty: true,
                              })
                            }
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Group>
                  <Divider />
                </Box>

                {allLanguages.map((lang) => {
                  return (
                    <Box key={`lang-${lang}`}>
                      <TextInput
                        label={`Title${showLanguageLabels ? ` (${lang.toUpperCase()})` : ""}`}
                        name={`titleI18n.${lang}`}
                        control={control}
                        disabled={!enableActions}
                        size="sm"
                        placeholder="Session title"
                        withAsterisk
                      />
                      <Box mt="md">
                        <Text size="sm" fw={500} mb="xs">
                          Description
                          {showLanguageLabels ? ` (${lang.toUpperCase()})` : ""}
                        </Text>
                        <Controller
                          name={`descriptionRichI18n.${lang}`}
                          control={control}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <SessionDescriptionEditor
                              onChange={onChange}
                              content={value}
                              error={error}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  );
                })}

                {locationsOptions?.length > 0 ? (
                  <Select
                    name="locationId"
                    label="Location"
                    placeholder="Select location"
                    data={locationsOptions}
                    control={control}
                    withAsterisk
                    disabled={!enableActions}
                    searchable
                    size="sm"
                    leftSection={<IconMapPin size={16} />}
                  />
                ) : (
                  <Text>
                    No locations found. Please{" "}
                    <Anchor component={Link} to="/app/sessions/locations">
                      add locations
                    </Anchor>{" "}
                    first to create a session.
                  </Text>
                )}

                {event?.dates?.length > 0 ? (
                  <Box>
                    <Grid>
                      <Grid.Col span={6}>
                        <DateTimePicker
                          label="Starting"
                          placeholder="Starting"
                          name="starting"
                          onChange={setStart}
                          withAsterisk
                          clearable
                          disabled={!enableActions}
                          excludeDate={(date) => {
                            const allowedDates = event?.dates.map(
                              (date) => date.date
                            );
                            return !allowedDates.includes(
                              dayjs(date).format("YYYY-MM-DD")
                            );
                          }}
                          hideOutsideDates
                          control={control}
                          size="sm"
                          leftSection={<IconCalendarTime size={16} />}
                          styles={{
                            input: {
                              "&:focus": {
                                borderColor: "var(--mantine-color-blue-6)",
                              },
                            },
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <DateTimePicker
                          label="Ending"
                          placeholder="Ending"
                          name="ending"
                          onChange={setEnd}
                          withAsterisk
                          clearable
                          disabled={!enableActions}
                          excludeDate={(date) => {
                            const allowedDates = event?.dates.map(
                              (date) => date.date
                            );
                            return (
                              !allowedDates.includes(
                                dayjs(date).format("YYYY-MM-DD")
                              ) ||
                              (dayjs(date).isBefore(start) &&
                                dayjs(date).format("YYYY-MM-DD") !==
                                  dayjs(start).format("YYYY-MM-DD"))
                            );
                          }}
                          control={control}
                          size="sm"
                          leftSection={<IconCalendarTime size={16} />}
                          styles={{
                            input: {
                              "&:focus": {
                                borderColor: "var(--mantine-color-blue-6)",
                              },
                            },
                          }}
                        />
                      </Grid.Col>
                    </Grid>
                  </Box>
                ) : (
                  <Text>
                    No event dates found. Please{" "}
                    <Anchor component={Link} to="/app/events/customize">
                      add event dates
                    </Anchor>{" "}
                    first to create a session.
                  </Text>
                )}

                <MultiSelect
                  label="Speakers"
                  name="speakers"
                  control={control}
                  data={speakerOptions}
                  searchable
                  limit={10}
                  size="sm"
                  nothingFoundMessage="No speakers found"
                  placeholder="Select speakers"
                  leftSection={<IconUsers size={16} />}
                  styles={{
                    input: {
                      "&:focus": {
                        borderColor: "var(--mantine-color-blue-6)",
                      },
                    },
                  }}
                />

                <Select
                  label="Sponsor"
                  name="sponsorId"
                  control={control}
                  data={sponsorOptions}
                  defaultValue={defaultSponsor?.value.toString()}
                  clearable
                  searchable
                  nothingFoundMessage="Nothing found"
                  placeholder="Select sponsor"
                  size="sm"
                  leftSection={<IconBuilding size={16} />}
                />
              </Stack>
            </Paper>
          </Grid.Col>

          {/* Right Column - Related Information */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="md">
              <Paper withBorder shadow="md" p="xl" radius="md">
                <Stack gap="md">
                  <Box>
                    <Title
                      order={3}
                      mb="md"
                      display="flex"
                      style={{ alignItems: "center", gap: "8px" }}
                    >
                      <IconUsers size={24} />
                      Related Information
                    </Title>
                    <Divider />
                  </Box>

                  <Box>
                    <Title
                      order={4}
                      mb="md"
                      display="flex"
                      style={{ alignItems: "center", gap: "8px" }}
                    >
                      <IconEye size={20} />
                      Visibility
                    </Title>
                    <MultiSelect
                      control={control}
                      name="targetedTagIds"
                      label="Visible to"
                      description="Leave empty to show this session to everyone."
                      placeholder="Visible to everyone"
                      data={categorySelectOptions}
                      disabled={!enableActions}
                      searchable
                      clearable
                      nothingFoundMessage="No attendee categories found"
                      size="sm"
                    />
                  </Box>

                  <Box>
                    <Title
                      order={4}
                      mb="md"
                      display="flex"
                      style={{ alignItems: "center", gap: "8px" }}
                    >
                      <IconTags size={20} />
                      Tags
                    </Title>
                    {tagOptions?.length > 0 ? (
                      <Stack gap="md">
                        {tagSubtypes.map((type) => (
                          <MultiSelect
                            control={control}
                            key={type}
                            label={`${formatTagLabel(type)}`}
                            name={`tags.${type}`}
                            placeholder={`Select ${formatTagLabel(type)} tags`}
                            data={tagSelectOptions[type] || []}
                            defaultValue={defaultTags[type] || []}
                            searchable
                            nothingFoundMessage="No tags found"
                            clearable
                            size="sm"
                            styles={{
                              input: {
                                "&:focus": {
                                  borderColor: "var(--mantine-color-blue-6)",
                                },
                              },
                            }}
                            onChange={(value) =>
                              setValue(`tags.${type}`, value, {
                                shouldDirty: true,
                              })
                            }
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Text>
                        No tags found. Please{" "}
                        <Anchor component={Link} to="/app/tags">
                          create tags
                        </Anchor>{" "}
                        first to add them to the session.
                      </Text>
                    )}
                  </Box>
                </Stack>
              </Paper>

              {/* Session Links Section */}
              {mode === "edit" && (
                <Paper withBorder shadow="md" p="xl" radius="md">
                  <Stack gap="md">
                    <Box>
                      <Group justify="space-between" align="center">
                        <Title
                          order={3}
                          display="flex"
                          style={{ alignItems: "center", gap: "8px" }}
                        >
                          <IconLink size={24} />
                          Session Links
                        </Title>
                        <Button
                          leftSection={<IconPlus size={16} />}
                          size="sm"
                          onClick={() => setIsAddLinkModalOpen(true)}
                        >
                          Add Link
                        </Button>
                      </Group>
                      <Divider mt="md" />
                    </Box>

                    {/* Existing Links List */}
                    {links?.length > 0 ? (
                      <Stack gap="xs">
                        {links.map((link) => (
                          <SessionLinkCard
                            link={link}
                            sessionId={data.id}
                            onDeleteLink={onDeleteLink}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Text c="dimmed" size="sm">
                        No links added yet
                      </Text>
                    )}
                  </Stack>
                </Paper>
              )}

              {mode === "edit" && (
                <AddLinkModal
                  opened={isAddLinkModalOpen}
                  onClose={() => setIsAddLinkModalOpen(false)}
                  onSubmit={handleAddLink}
                />
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        {!enableActions && (
          <Text size="sm" c="red" mt="md">
            Only system admins can {mode === "create" ? "create" : "edit"}{" "}
            sessions!
          </Text>
        )}
      </form>
    </Container>
  );
}

export default SessionForm;
